import type * as React from 'react';
import { cn } from '@/lib/utils';
import { useFieldControl } from '@/ui/components/Field/fieldContext';

// shadcn/ui (AD #39): shadcn's Input. `disabled` renders as `aria-disabled` plus `readOnly`, never the
// bare attribute, so the field stays focusable and announced (design-system §5 Disabled row). Inside a
// `Field` it takes the field's id, `aria-describedby`, `aria-invalid` and `required`, and reports change and
// blur to it, as the src/ui Input does — so a Field's label, helper and error wire up unchanged.
function Input({
    className,
    type,
    disabled = false,
    readOnly,
    id,
    required,
    'aria-describedby': describedBy,
    'aria-invalid': ownInvalid,
    onBlur,
    onChange,
    ...props
}: React.ComponentProps<'input'>) {
    const field = useFieldControl({
        id,
        'aria-describedby': describedBy,
        invalid: ownInvalid === true || ownInvalid === 'true',
        ...(required === undefined ? {} : { required }),
    });
    return (
        <input
            /* testid-exempt: a shadcn primitive; the caller passes data-testid, a label or text through props */
            type={type}
            data-slot="input"
            id={field.id}
            required={field.required}
            aria-describedby={field['aria-describedby']}
            aria-invalid={field['aria-invalid']}
            aria-disabled={disabled ? true : undefined}
            readOnly={disabled || readOnly}
            onBlur={(event) => {
                onBlur?.(event);
                field.reportBlur(event.target.value);
            }}
            onChange={(event) => {
                if (disabled) {
                    return;
                }
                onChange?.(event);
                field.reportChange(event.target.value);
            }}
            className={cn(
                'h-9 w-full min-w-0 rounded-md border border-input bg-card px-3 py-1 text-sm shadow-xs transition-[color,box-shadow] selection:bg-primary selection:text-primary-foreground file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground aria-disabled:cursor-not-allowed aria-disabled:bg-secondary aria-disabled:text-muted-foreground',
                'aria-invalid:border-destructive',
                className,
            )}
            {...props}
        />
    );
}

export { Input };
